"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useMemo } from "react";
import UserCICAndLevel, { UserCICAndLevelSize } from "@/components/user/utils/UserCICAndLevel";
import Link from "next/link";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import type { ApiDropPart } from "@/generated/models/ApiDropPart";
import DropPartMarkdownWithPropLogger from "@/components/drops/view/part/DropPartMarkdownWithPropLogger";
import WaveDropTime from "./time/WaveDropTime";
import { getWaveRoute } from "@/helpers/navigation.helpers";
import Image from "next/image";
import { resolveIpfsUrlSync } from "@/components/ipfs/IPFSContext";
import { useLinkPreviewContext } from "@/components/waves/LinkPreviewContext";
import DropNotFound from "./DropNotFound";
import { areSameProfileIdentity } from "@/helpers/ProfileHelpers";
import { useWaveDropQuoteDisplay, WaveDropQuoteDisplayProvider } from "./WaveDropQuoteDisplayContext";
interface WaveDropQuoteProps {
  readonly drop: ApiDrop | null;
  readonly partId: number;
  readonly onQuoteClick: (drop: ApiDrop) => void;
  readonly isNotFound?: boolean;
  readonly embedPath?: readonly string[] | undefined;
  readonly quotePath?: readonly string[] | undefined;
  readonly embedDepth?: number | undefined;
  readonly maxEmbedDepth?: number | undefined;
  readonly onLinkCardActionsActiveChange?: ((href: string, active: boolean) => void) | undefined;
}
interface WaveDropQuoteProfilePictureProps {
  readonly drop: ApiDrop | null;
}
const WaveDropQuoteProfilePicture: React.FC<WaveDropQuoteProfilePictureProps> = t0 => {
  const $ = _c(7);
  const {
    drop
  } = t0;
  if (!drop) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <div className="tw-h-full tw-w-full tw-max-w-full tw-animate-pulse tw-overflow-hidden tw-rounded-md tw-bg-iron-900" />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  let t1;
  if ($[1] !== drop.author.pfp) {
    t1 = drop.author.pfp ? resolveIpfsUrlSync(drop.author.pfp) : null;
    $[1] = drop.author.pfp;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const resolvedPfp = t1;
  if (resolvedPfp) {
    const t2 = `${drop.author.handle ?? drop.author.primary_address}'s profile picture`;
    let t3;
    if ($[3] !== resolvedPfp || $[4] !== t2) {
      t3 = <div className="tw-relative tw-h-full tw-w-full"><Image src={resolvedPfp} alt={t2} fill={true} sizes="24px" className="tw-rounded-md tw-bg-transparent tw-object-cover" /></div>;
      $[3] = resolvedPfp;
      $[4] = t2;
      $[5] = t3;
    } else {
      t3 = $[5];
    }
    return t3;
  }
  let t2;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="tw-h-full tw-w-full tw-max-w-full tw-overflow-hidden tw-rounded-md tw-bg-iron-900" />;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
};
function WaveDropQuoteBody(t0) {
  const $ = _c(28);
  const {
    drop,
    quoteAuthorLabel,
    quoteContent,
    waveHref
  } = t0;
  let t1;
  if ($[0] !== drop) {
    t1 = <div className="tw-relative tw-h-6 tw-w-6 tw-flex-shrink-0 tw-rounded-md tw-bg-iron-900"><div className="tw-h-full tw-w-full tw-rounded-md"><WaveDropQuoteProfilePicture drop={drop} /></div></div>;
    $[0] = drop;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== quoteAuthorLabel) {
    t2 = quoteAuthorLabel && <p className="tw-mb-0 tw-text-md tw-font-semibold tw-leading-none"><Link href={`/${quoteAuthorLabel}`} className="tw-text-iron-200 tw-no-underline tw-transition tw-duration-300 tw-ease-out hover:tw-text-iron-500">{quoteAuthorLabel}</Link></p>;
    $[2] = quoteAuthorLabel;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== drop) {
    t3 = !!drop && <UserCICAndLevel level={drop.author.level} size={UserCICAndLevelSize.SMALL} />;
    $[4] = drop;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2 || $[7] !== t3) {
    t4 = <div className="tw-flex tw-items-center tw-gap-x-2">{t2}{t3}</div>;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== drop) {
    t5 = !!drop && <><div className="tw-size-[3px] tw-flex-shrink-0 tw-rounded-full tw-bg-iron-600" /><WaveDropTime timestamp={drop.created_at} /></>;
    $[9] = drop;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t4 || $[12] !== t5) {
    t6 = <div className="tw-flex tw-items-center tw-gap-x-2">{t4}{t5}</div>;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== drop || $[15] !== waveHref) {
    t7 = drop && waveHref && <Link href={waveHref} className="tw-leading-0 -tw-mt-1 tw-text-[11px] tw-text-iron-500 tw-no-underline tw-transition tw-duration-300 tw-ease-out hover:tw-text-iron-300">{drop.wave.name}</Link>;
    $[14] = drop;
    $[15] = waveHref;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== t7) {
    t8 = <div>{t7}</div>;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== quoteContent) {
    t9 = <div className="tw-mt-0.5">{quoteContent}</div>;
    $[19] = quoteContent;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== t6 || $[22] !== t8 || $[23] !== t9) {
    t10 = <div className="tw-flex tw-w-full tw-flex-col">{t6}{t8}{t9}</div>;
    $[21] = t6;
    $[22] = t8;
    $[23] = t9;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== t1 || $[26] !== t10) {
    t11 = <div className="tw-group tw-relative tw-flex tw-w-full tw-flex-col"><div className="tw-flex tw-gap-x-2">{t1}{t10}</div></div>;
    $[25] = t1;
    $[26] = t10;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  return t11;
}
const WaveDropQuote: React.FC<WaveDropQuoteProps> = t0 => {
  const $ = _c(56);
  const {
    drop,
    partId,
    onQuoteClick,
    isNotFound: t1,
    embedPath,
    quotePath,
    embedDepth,
    maxEmbedDepth,
    onLinkCardActionsActiveChange
  } = t0;
  const isNotFound = t1 === undefined ? false : t1;
  const {
    onCardActionsActiveChange
  } = useLinkPreviewContext();
  const {
    flattenWhenAuthorSameAs
  } = useWaveDropQuoteDisplay();
  let t2;
  bb0: {
    if (!drop) {
      t2 = null;
      break bb0;
    }
    let t3;
    if ($[0] !== drop.parts || $[1] !== partId) {
      t3 = drop.parts.find(part => part.part_id === partId) ?? null;
      $[0] = drop.parts;
      $[1] = partId;
      $[2] = t3;
    } else {
      t3 = $[2];
    }
    t2 = t3;
  }
  const quotedPart = t2;
  let t3;
  if ($[3] !== drop || $[4] !== onQuoteClick) {
    t3 = () => {
      if (drop?.wave.id && drop.id) {
        onQuoteClick(drop);
      }
    };
    $[3] = drop;
    $[4] = onQuoteClick;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const goToQuoteDrop = t3;
  let t4;
  bb1: {
    if (!drop) {
      t4 = "";
      break bb1;
    }
    const waveDetails = drop.wave as unknown as {
      chat?: {
        scope?: {
          group?: {
            is_direct_message?: boolean | undefined;
          } | undefined;
        } | undefined;
      } | undefined;
    };
    const isDirectMessage = waveDetails.chat?.scope?.group?.is_direct_message ?? false;
    let t5;
    if ($[6] !== drop.wave.id || $[7] !== isDirectMessage) {
      t5 = getWaveRoute({
        waveId: drop.wave.id,
        isDirectMessage,
        isApp: false
      });
      $[6] = drop.wave.id;
      $[7] = isDirectMessage;
      $[8] = t5;
    } else {
      t5 = $[8];
    }
    t4 = t5;
  }
  const waveHref = t4;
  let path;
  if ($[9] !== drop || $[10] !== quotePath) {
    path = quotePath ? [...quotePath] : [];
    if (drop?.wave.id) {
      const currentQuoteKey = `${drop.wave.id}:${drop.serial_no}`;
      if (!path.includes(currentQuoteKey)) {
        path.push(currentQuoteKey);
      }
    }
    $[9] = drop;
    $[10] = quotePath;
    $[11] = path;
  } else {
    path = $[11];
  }
  const effectiveQuotePath = path;
  const resolvedOnLinkCardActionsActiveChange = onLinkCardActionsActiveChange ?? onCardActionsActiveChange;
  const isInteractive = drop !== null && !isNotFound;
  const shouldFlattenQuote = areSameProfileIdentity({
    left: drop?.author,
    right: flattenWhenAuthorSameAs
  });
  const quoteAuthorLabel = drop ? drop.author.handle ?? drop.author.primary_address : null;
  let t5;
  if ($[12] !== goToQuoteDrop || $[13] !== isInteractive) {
    t5 = event => {
      event.stopPropagation();
      if (isInteractive) {
        goToQuoteDrop();
      }
    };
    $[12] = goToQuoteDrop;
    $[13] = isInteractive;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const handleQuoteContainerClick = t5;
  let t6;
  if ($[15] !== goToQuoteDrop || $[16] !== isInteractive) {
    t6 = isInteractive ? event_0 => {
      if (event_0.key === "Enter" || event_0.key === " ") {
        event_0.preventDefault();
        event_0.stopPropagation();
        goToQuoteDrop();
      }
    } : undefined;
    $[15] = goToQuoteDrop;
    $[16] = isInteractive;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  const handleQuoteContainerKeyDown = t6;
  const quoteContainerClassName = `tw-mt-1 ${isInteractive ? "tw-cursor-pointer" : ""} tw-rounded-xl tw-bg-iron-950 tw-px-3 tw-py-3 tw-ring-1 tw-ring-inset tw-ring-iron-800`;
  if (isNotFound) {
    let t7;
    if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <DropNotFound />;
      $[18] = t7;
    } else {
      t7 = $[18];
    }
    let t8;
    if ($[19] !== handleQuoteContainerClick || $[20] !== quoteContainerClassName) {
      t8 = <div className={quoteContainerClassName} onClick={handleQuoteContainerClick}>{t7}</div>;
      $[19] = handleQuoteContainerClick;
      $[20] = quoteContainerClassName;
      $[21] = t8;
    } else {
      t8 = $[21];
    }
    return t8;
  }
  const t7 = quotedPart?.content ?? "";
  let t8;
  if ($[22] !== drop?.mentioned_users) {
    t8 = drop?.mentioned_users ?? [];
    $[22] = drop?.mentioned_users;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  let t9;
  if ($[24] !== drop?.mentioned_groups) {
    t9 = drop?.mentioned_groups ?? [];
    $[24] = drop?.mentioned_groups;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] !== drop?.mentioned_waves) {
    t10 = drop?.mentioned_waves ?? [];
    $[26] = drop?.mentioned_waves;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== drop?.referenced_nfts) {
    t11 = drop?.referenced_nfts ?? [];
    $[28] = drop?.referenced_nfts;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  const t12 = drop?.nft_links;
  const t13 = drop?.id;
  let t14;
  if ($[30] !== effectiveQuotePath || $[31] !== embedDepth || $[32] !== embedPath || $[33] !== maxEmbedDepth || $[34] !== onQuoteClick || $[35] !== resolvedOnLinkCardActionsActiveChange || $[36] !== t10 || $[37] !== t11 || $[38] !== t12 || $[39] !== t13 || $[40] !== t7 || $[41] !== t8 || $[42] !== t9) {
    t14 = <WaveDropQuoteDisplayProvider flattenWhenAuthorSameAs={null}><DropPartMarkdownWithPropLogger partContent={t7} mentionedUsers={t8} mentionedGroups={t9} mentionedWaves={t10} referencedNfts={t11} nftLinks={t12} textSize="sm" onQuoteClick={onQuoteClick} currentDropId={t13} embedPath={embedPath} quotePath={effectiveQuotePath} embedDepth={embedDepth} maxEmbedDepth={maxEmbedDepth} onLinkCardActionsActiveChange={resolvedOnLinkCardActionsActiveChange} /></WaveDropQuoteDisplayProvider>;
    $[30] = effectiveQuotePath;
    $[31] = embedDepth;
    $[32] = embedPath;
    $[33] = maxEmbedDepth;
    $[34] = onQuoteClick;
    $[35] = resolvedOnLinkCardActionsActiveChange;
    $[36] = t10;
    $[37] = t11;
    $[38] = t12;
    $[39] = t13;
    $[40] = t7;
    $[41] = t8;
    $[42] = t9;
    $[43] = t14;
  } else {
    t14 = $[43];
  }
  const quoteContent = t14;
  if (shouldFlattenQuote) {
    return quoteContent;
  }
  const t15 = isInteractive ? "button" : undefined;
  const t16 = isInteractive ? 0 : undefined;
  let t17;
  if ($[44] !== drop || $[45] !== quoteAuthorLabel || $[46] !== quoteContent || $[47] !== waveHref) {
    t17 = <WaveDropQuoteBody drop={drop} quoteAuthorLabel={quoteAuthorLabel} quoteContent={quoteContent} waveHref={waveHref} />;
    $[44] = drop;
    $[45] = quoteAuthorLabel;
    $[46] = quoteContent;
    $[47] = waveHref;
    $[48] = t17;
  } else {
    t17 = $[48];
  }
  let t18;
  if ($[49] !== handleQuoteContainerClick || $[50] !== handleQuoteContainerKeyDown || $[51] !== quoteContainerClassName || $[52] !== t15 || $[53] !== t16 || $[54] !== t17) {
    t18 = <div className={quoteContainerClassName} onClick={handleQuoteContainerClick} onKeyDown={handleQuoteContainerKeyDown} role={t15} tabIndex={t16}>{t17}</div>;
    $[49] = handleQuoteContainerClick;
    $[50] = handleQuoteContainerKeyDown;
    $[51] = quoteContainerClassName;
    $[52] = t15;
    $[53] = t16;
    $[54] = t17;
    $[55] = t18;
  } else {
    t18 = $[55];
  }
  return t18;
};
export default WaveDropQuote;